import React from 'react';
import {
  PageHeader as ToqioPageHeader,
  PageHeaderActions as ToqioPageHeaderActions,
  PageHeaderInfo as ToqioPageHeaderInfo,
  PageHeaderInfoText as ToqioPageHeaderInfoText,
  spacing,
  type PageHeaderProps as ToqioPageHeaderProps,
  type PageHeaderContentProps,
} from '@toqio/toqio-web-components';
import { BackButton } from './BackButton';
import { COMPONENT_TYPE, getComponentTestIdProp } from '../testIds';

export type PageHeaderProps = Pick<
  ToqioPageHeaderProps,
  'avatar' | 'title' | 'actions' | 'info' | 'showDivider' | 'className'
> & {
  testId?: string;
  /** Renders a "Back" link above the header (wired to `onClickBack`). */
  showBackButton?: boolean;
  onClickBack?: () => void;
  /** Override the back link's default "Back" label (e.g. a flow-specific one). */
  backLabel?: React.ReactNode;
  /**
   * Spacing below the header in DS units (×4px). Defaults to 8 (32px), the
   * lum-web standard. Set per flow when matching a Flowable original that uses
   * a different rhythm (e.g. card-payment-sca → 6 / 24px, SEB-1615).
   */
  bottomSpacing?: number;
  topSpacing?: number;
};

/**
 * Shared page header: thin wrapper over the DS `PageHeader` that bakes in the
 * standard vertical spacing, the optional back link and testId conventions,
 * mirroring lum-web's `PageHeader`.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  showDivider = true,
  showBackButton = false,
  onClickBack,
  backLabel,
  bottomSpacing = 8,
  topSpacing = 8,
  ...rest
}) => {
  const testIdProps = getComponentTestIdProp(COMPONENT_TYPE.PAGE_HEADER, rest.testId, title);
  const backButtonTestId = testIdProps.testId
    ? `${testIdProps.testId}-back-btn`
    : `${COMPONENT_TYPE.PAGE_HEADER}-back-btn`;

  // Wrap in a single block so the host page's flex `gap` can't intrude between
  // the back link and the header; internal spacing is governed solely by the
  // baked margins (block-flow margin collapsing), matching lum-web.
  return (
    <div>
      {showBackButton && (
        <div data-testid={backButtonTestId} style={spacing('my', 2, true)}>
          <BackButton onClick={() => onClickBack?.()} label={backLabel} />
        </div>
      )}
      <ToqioPageHeader
        {...rest}
        {...testIdProps}
        title={title}
        showDivider={showDivider}
        style={
          showBackButton
            ? spacing('mb', bottomSpacing, true)
            : { ...spacing('mt', topSpacing, true), ...spacing('mb', bottomSpacing, true) }
        }
      />
    </div>
  );
};

export const PageHeaderActions: React.FC<PageHeaderContentProps> = ({ testId, ...props }) => (
  <ToqioPageHeaderActions {...props} testId={testId ?? `${COMPONENT_TYPE.PAGE_HEADER}-actions`} />
);

export const PageHeaderInfo: React.FC<PageHeaderContentProps> = ({ testId, ...props }) => (
  <ToqioPageHeaderInfo {...props} testId={testId ?? `${COMPONENT_TYPE.PAGE_HEADER}-info`} />
);

export const PageHeaderInfoText: React.FC<PageHeaderContentProps> = ({ testId, ...props }) => (
  <ToqioPageHeaderInfoText {...props} testId={testId ?? `${COMPONENT_TYPE.PAGE_HEADER}-info-text`} />
);
